<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { historyCommonConfig } from './configs/common'
import { api } from '@/api'
import { useReadStore, useToastStore, useTrackValueStore } from '@/stores'
import { TRACK_CHANNEL_NAME_VALUE } from '@/constants'
import { genPlayTraceId } from '@/utils/playTraceId'
import { useElementClick } from '@/composables/tracks/useElementClick'

type HistoryBook = GetReadHistoryListResponse['data']['records'][number]
type LibraryBook = GetBookshelfListResponse['data']['records'][number]
type ShelfTab = 'favorites' | 'history'

const PAGE_SIZE = 20

const config = historyCommonConfig

const { $toast } = useToastStore()
const { elementClick } = useElementClick()
const readStore = useReadStore()

interface PagedState<T> {
  items: T[]
  page: number
  total: number
  fetching: boolean
  ending: boolean
  error: any
}

function createPagedState<T>(): PagedState<T> {
  return reactive({
    items: [],
    page: 1,
    total: 0,
    fetching: false,
    ending: false,
    error: null,
  }) as PagedState<T>
}

const favorites = createPagedState<LibraryBook>()
const history = createPagedState<HistoryBook>()

const activeTab = ref<ShelfTab>('favorites')

// 继续阅读大卡
const lastPlay = ref<GetReadLastPlayResponse['data'] | null>(null)

async function fetchLastPlay() {
  try {
    const { data } = await api.get<any, GetReadLastPlayResponse>('/read/last/play')
    if (data?.contentId)
      lastPlay.value = data
  }
  catch {
    lastPlay.value = null
  }
}

async function fetchFavorites() {
  favorites.fetching = true
  try {
    const { data } = await api.get<any, GetBookshelfListResponse>('/bookshelf/list', {
      params: { pageIndex: favorites.page, pageSize: PAGE_SIZE },
    })
    const next = [...favorites.items, ...(data.records || [])]
    // 与小说项目保持一致：按最近阅读时间倒序
    next.sort((a, b) => {
      const ta = a.lastReadTime ? new Date(a.lastReadTime).getTime() : 0
      const tb = b.lastReadTime ? new Date(b.lastReadTime).getTime() : 0
      return tb - ta
    })
    favorites.items = next
    favorites.total = data.total || 0
    if (favorites.page >= (data.pages || 0))
      favorites.ending = true
    else
      favorites.page += 1
  }
  catch (e) {
    favorites.error = e
  }
  finally {
    favorites.fetching = false
  }
}

async function fetchHistory() {
  history.fetching = true
  try {
    const { data } = await api.get<any, GetReadHistoryListResponse>('/read/history/list', {
      params: { pageIndex: history.page, pageSize: PAGE_SIZE },
    })
    history.items = [...history.items, ...(data.records || [])]
    history.total = data.total || 0
    if (history.page >= (data.pages || 0))
      history.ending = true
    else
      history.page += 1
  }
  catch (e) {
    history.error = e
  }
  finally {
    history.fetching = false
  }
}

const currentState = computed(() => activeTab.value === 'favorites' ? favorites : history)

async function loadCurrent() {
  const state = currentState.value
  if (state.fetching || state.ending || state.error)
    return
  if (activeTab.value === 'favorites')
    await fetchFavorites()
  else
    await fetchHistory()
}

function switchTab(tab: ShelfTab) {
  if (activeTab.value === tab)
    return
  elementClick(tab === 'favorites' ? 'shelf_favorites' : 'shelf_history')
  activeTab.value = tab
  // 首次进入该页签时拉取
  const state = currentState.value
  if (!state.items.length && !state.fetching && !state.ending)
    loadCurrent()
}

const infiniteRef = ref<HTMLElement>()
useInfiniteScroll(infiniteRef, async () => {
  await loadCurrent()
}, { distance: 50 })

const route = useRoute()
const from = computed(() => {
  return route.query.from as string
})

const router = useRouter()

interface PlayableBook {
  contentId: string
  contentChapterId?: string
  contentCoverUrl?: string
  offlineFlag?: boolean
}

function gotoChapter(item: PlayableBook, chlName: TRACK_CHANNEL_NAME_VALUE) {
  if (item.offlineFlag)
    return $toast(config.texts.bookRemoved)
  readStore.update({
    contentId: item.contentId,
    chapterId: item.contentChapterId || '',
  })
  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId: item.contentId, firstOrigin: 4, originName: 4, chlName })

  if (item.contentCoverUrl)
    sessionStorage.setItem('gameCoverUrl', item.contentCoverUrl)

  router.push({
    name: 'game',
    query: {
      contentId: item.contentId,
      playTraceId: genPlayTraceId(),
      coverUrl: item.contentCoverUrl || '',
    },
  })
}

function playLast() {
  if (!lastPlay.value)
    return
  elementClick('continue_play')
  gotoChapter(lastPlay.value, TRACK_CHANNEL_NAME_VALUE.READ_HISTORY)
}

function gotoBook(item: HistoryBook | LibraryBook) {
  gotoChapter(item as PlayableBook, TRACK_CHANNEL_NAME_VALUE.READ_HISTORY)
}

// 书卡进度：全书进度 = (已读完章节数 + 当前章节内进度) / 总章节数
// currentNumber/maxNumber 为章内对话进度（WGSQ-476 新接口字段）；缺省退化为纯章节进度
function bookProgress(item: any): number {
  if (item.sequenceNo && item.chapterNum) {
    const chapterInner = item.maxNumber ? Math.min(1, (item.currentNumber || 0) / item.maxNumber) : 0
    const percent = ((item.sequenceNo - 1 + chapterInner) / item.chapterNum) * 100
    // 保底 6%，避免刚开局时进度条视觉上不可见
    return Math.min(100, Math.max(6, Math.round(percent)))
  }
  if (typeof item.readProgress === 'number')
    return Math.min(100, Math.max(0, item.readProgress))
  return 0
}

function chapterLabel(item: any): string {
  const no = item.sequenceNo || 1
  return config.texts.chapterOf(no, item.chapterNum)
}

onMounted(() => {
  fetchLastPlay()
  // 两个列表都先拉首页，保证页签计数初始即展示
  fetchFavorites()
  fetchHistory()
})
</script>

<template>
  <div ref="infiniteRef" class="pb-90px h-100dvh overflow-scroll bg-n-bg-global no-scrollbar">
    <!-- Tab 页：显示品牌 Header + 钻石入口 -->
    <div v-if="!from" class="brand-header sticky top-0 z-10 flex items-center justify-between px-15px bg-n-bg-global">
      <img src="/images/vowpath/home/logo.png" class="brand-logo" alt="Vowpath">
      <CoinEntry />
    </div>
    <!-- 二级页：显示返回导航 -->
    <div v-if="from">
      <BasicNavbar />
      <div class="h-5" />
    </div>

    <!-- 继续阅读大卡 -->
    <div v-if="lastPlay" class="px-15px mt-6px">
      <div class="hero-card relative rounded-12px overflow-hidden" @click="playLast">
        <BizBookCover
          :id="`lastplay-${lastPlay.contentId}`"
          :src="lastPlay.contentCoverUrl || ''"
          :is-report-imp="false"
          class="hero-cover w-full"
        />
        <!-- 底部阅读进度条（WGSQ-476） -->
        <div v-if="bookProgress(lastPlay)" class="cover-progress">
          <div class="cover-progress-inner" :style="{ width: `${bookProgress(lastPlay)}%` }" />
        </div>
      </div>
      <div class="flex items-center justify-between mt-10px">
        <div class="min-w-0 pr-12px">
          <p class="text-16px font-bold text-n-text-1 truncate">
            {{ lastPlay.contentName }}
          </p>
          <p class="text-12px mt-4px text-n-text-2 truncate">
            {{ config.texts.chapterOf(lastPlay.sequenceNo || 1) }}
            <span v-if="lastPlay.contentChapterName"> · {{ lastPlay.contentChapterName }}</span>
          </p>
        </div>
        <div class="play-btn shrink-0" @click="playLast">
          {{ config.texts.play }}
        </div>
      </div>
    </div>

    <!-- Favorites / History 双页签 -->
    <div class="flex items-center px-15px mt-18px">
      <div class="shelf-tab" :class="{ active: activeTab === 'favorites' }" @click="switchTab('favorites')">
        <span>{{ config.texts.favoritesTab }}</span>
        <span class="tab-count">({{ favorites.total }})</span>
        <i v-if="activeTab === 'favorites'" class="tab-underline" />
      </div>
      <div class="tab-divider mx-16px" />
      <div class="shelf-tab" :class="{ active: activeTab === 'history' }" @click="switchTab('history')">
        <span>{{ config.texts.historyTab }}</span>
        <span class="tab-count">({{ history.total }})</span>
        <i v-if="activeTab === 'history'" class="tab-underline" />
      </div>
    </div>

    <!-- 书籍三列网格 -->
    <div class="px-15px mt-14px">
      <!-- 空态 -->
      <div v-if="!currentState.fetching && currentState.items.length === 0" class="pt-10vh w-full text-center flex flex-col justify-center">
        <div>
          <img class="mx-a w-100px" src="@/assets/cover-empty.png" alt="empty">
          <p class="mt-14px text-13px text-n-text-2">
            {{ config.texts.noData }}
          </p>
          <div class="mt-30px">
            <router-link
              :to="{ name: 'home' }" replace
              class="inline-block text-n-primary text-14px py-2 px-4 border border-solid border-n-primary rounded-full"
            >
              {{ config.texts.goToNovel }}
            </router-link>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="grid grid-cols-3 gap-x-10px gap-y-16px">
          <div
            v-for="item in currentState.items"
            :key="item.contentId"
            class="cursor-pointer"
            @click="gotoBook(item)"
          >
            <div class="relative rounded-8px overflow-hidden">
              <BizBookCover
                :id="`shelf-${item.contentId}`"
                :src="item.contentCoverUrl || ''"
                :is-report-imp="false"
                class="shelf-cover w-full"
              />
              <!-- 底部进度条 -->
              <div v-if="bookProgress(item)" class="cover-progress">
                <div class="cover-progress-inner" :style="{ width: `${bookProgress(item)}%` }" />
              </div>
            </div>
            <p class="mt-8px text-13px font-500 line-clamp-2 text-n-text-1 leading-17px">
              {{ item.contentName }}
            </p>
            <p class="mt-4px text-11px text-n-text-2 truncate">
              {{ chapterLabel(item) }}
            </p>
          </div>
        </div>

        <div v-if="currentState.ending && currentState.items.length" class="py-10 flex space-x-2.5 items-center justify-center">
          <span class="text-13px text-n-text-2">{{ config.texts.noMore }}</span>
        </div>
        <div v-else-if="currentState.fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-2">
          <div class="animate-spin">
            <i-icon-park-outline-loading />
          </div>
          <p>{{ config.texts.loading }}</p>
        </div>
      </template>
    </div>

    <BasicTabbar v-if="!from" tab="history" />
  </div>
</template>

<style lang="scss" scoped>
.no-scrollbar {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.brand-header {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* 继续阅读大卡 */
.hero-cover {
  aspect-ratio: 345 / 220;
  object-fit: cover;
}

.play-btn {
  min-width: 89px;
  height: 32px;
  line-height: 32px;
  padding: 0 20px;
  border-radius: 30px;
  text-align: center;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  cursor: pointer;
}

/* 双页签 */
.shelf-tab {
  position: relative;
  padding-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: baseline;

  span {
    font-size: 15px;
    color: var(--vp-shelf-tab, #7b7994);
  }

  .tab-count {
    margin-left: 4px;
    font-size: 12px;
  }

  &.active span {
    color: #ffffff;
    font-weight: bold;
    font-size: 17px;
  }

  .tab-underline {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 23px;
    height: 4px;
    border-radius: 3px;
    background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  }
}

.tab-divider {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.2);
}

/* 三列书格 */
.shelf-cover {
  aspect-ratio: 105 / 140;
  object-fit: cover;
}

.cover-progress {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.35);
  overflow: hidden;
}

.cover-progress-inner {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
}
</style>
